/**
 * "Extra support — open spot" (D-1020-EXTRA, Eli 20 Oct 2026): a day with an open position (a cancellation, or simply room)
 * today or in the coming days lists the tenants on the Extra support list who could be offered it — under 4 visits that
 * month, fewest visits first, then longest since the last visit — each with Call (no number printed) and Book this spot.
 * The checkbox on the tenant's Housekeeping section is the director's standing OK; the press books it the usual way
 * (Scheduled, asks the tenant's confirmation — nothing is emailed without this press), and the visit is marked an
 * Approved Exception that does not count against the month's limit. The card is not shown when nobody could be offered a spot.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Role } from '@shared/roles';
import { EXTRA_SUPPORT_CAP, canBookExtraSupport, type OpenSpotDay, type SpotCandidate } from '@shared/extra-support';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CallButton } from '@/components/Contact';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { ApiRequestError } from '@/lib/api';
import { formatDayMedium, formatMonthDay } from '@/lib/dates';
import { postExtra, useOpenSpots, useRefreshExtra } from './api';

const DAYS_SHOWN = 4;

export function ExtraSpotsCard({ role, days = 7 }: { role: Role | null; days?: number }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const can = canBookExtraSupport(role);
  const q = useOpenSpots(can, days);
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useRefreshExtra();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  const spots = q.data?.days ?? [];
  if (!can || !spots.length) return null;
  const shown = all ? spots : spots.slice(0, DAYS_SHOWN);

  const book = async (d: OpenSpotDay, c: SpotCandidate) => {
    const key = `${d.dayKey}-${c.tenantId}`;
    setBusy(key);
    setError(null);
    try {
      const r = await postExtra({ kind: 'support', tenantId: c.tenantId, dayKey: d.dayKey, position: d.firstFree });
      const m = t('extra1020.spots.booked', { message: r.message });
      toast(m);
      announce(m);
      if (r.emailNotQueued) announce(r.emailNotQueued, 'assertive');
      refresh();
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card aria-labelledby="h-extra-spots" data-testid="extra-spots" style={{ marginTop: 18 }}>
      <CardHeader>
        <CardTitle id="h-extra-spots">{t('extra1020.spots.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="help">{t('extra1020.spots.help', { cap: EXTRA_SUPPORT_CAP })}</p>
        {error ? (
          <div className="warn take" role="alert" data-testid="extra-spots-error">
            {error}
          </div>
        ) : null}
        {shown.map((d) => (
          <div key={d.dayKey} data-testid={`extra-spot-${d.dayKey}`}>
            <h3 className="attn-group-h" id={`h-spot-${d.dayKey}`}>
              {formatDayMedium(d.dayKey, lang)} · {t('extra1020.spots.open', { count: d.free })}
            </h3>
            <ul className="attn-list" aria-labelledby={`h-spot-${d.dayKey}`}>
              {d.candidates.map((c) => (
                <li key={c.tenantId} data-testid={`extra-spot-${d.dayKey}-${c.tenantId}`}>
                  <div className="t">
                    <b>
                      <span className="tnum">{c.unit}</span> · {c.name}
                    </b>
                    <span>
                      {t('extra1020.spots.visits', { n: c.monthVisits, cap: EXTRA_SUPPORT_CAP })} · {c.lastVisitKey ? t('extra1020.spots.last', { day: formatMonthDay(c.lastVisitKey, lang) }) : t('extra1020.spots.noLast')}
                    </span>
                  </div>
                  <CallButton phone={c.phone} name={c.name} />
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy === `${d.dayKey}-${c.tenantId}`}
                    aria-label={t('extra1020.spots.bookLabel', { unit: c.unit, day: formatDayMedium(d.dayKey, lang) })}
                    data-testid={`extra-spot-book-${d.dayKey}-${c.tenantId}`}
                    onClick={() => book(d, c)}
                  >
                    {t('extra1020.spots.book')}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {spots.length > DAYS_SHOWN ? (
          <Button size="sm" variant="ghost" type="button" onClick={() => setAll((v) => !v)} aria-expanded={all}>
            {all ? t('extra1020.spots.fewer') : t('extra1020.spots.more', { count: spots.length - DAYS_SHOWN })}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
